import {
  Box,
  Chip,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import { useQuery } from '@tanstack/react-query';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Helmet } from 'react-helmet-async';
import { adminApi } from '../../api/adminApi';
import { PageHeader } from '../../components/PageHeader';
import { COMPETITION_STATUS_LABELS, type Competition, type CompetitionStatus } from '../../types/domain';
import { formatDateCz } from '../../utils/dateFormat';

const STATUS_COLOR: Record<CompetitionStatus, 'default' | 'success' | 'error'> = {
  DRAFT: 'default',
  LIVE: 'error',
  FINISHED: 'success',
};

/**
 * SUPER_ADMIN: Cross-unit prehled vsech soutezi.
 * Pouziva /api/admin/competitions (SUPER_ADMIN dostane vsechny kvuli role hierarchii).
 */
export const SuperAdminCompetitionsPage = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { data: comps = [], isLoading } = useQuery({
    queryKey: ['superadmin', 'allCompetitions'],
    queryFn: () => adminApi.listCompetitions(),
  });

  const columns: GridColDef<Competition>[] = [
    {
      field: 'shortCode',
      headerName: 'Kód',
      width: 110,
      renderCell: ({ row }) => (
        <Box sx={{ fontFamily: 'monospace', fontWeight: 600, fontSize: '0.9rem' }}>{row.shortCode}</Box>
      ),
    },
    { field: 'name', headerName: 'Název', flex: 1, minWidth: 220 },
    { field: 'unitName', headerName: 'Jednotka', width: 180 },
    {
      field: 'competitionDate',
      headerName: 'Datum',
      width: 130,
      renderCell: ({ row }) => formatDateCz(row.competitionDate),
    },
    {
      field: 'status',
      headerName: 'Stav',
      width: 130,
      renderCell: ({ row }) => (
        <Chip size="small" label={COMPETITION_STATUS_LABELS[row.status]} color={STATUS_COLOR[row.status]} />
      ),
    },
    {
      field: 'actions',
      headerName: 'Akce',
      width: 80,
      sortable: false,
      align: 'center',
      headerAlign: 'center',
      renderCell: ({ row }) => (
        <Tooltip title="Otevřít v administraci jednotky">
          <IconButton
            size="small"
            component={RouterLink}
            to={`/admin/competitions/${row.id}?unitId=${row.unitId}`}
            onClick={(e) => e.stopPropagation()}
          >
            <OpenInNewIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  return (
    <>
      <Helmet>
        <title>Všechny soutěže | SUPER ADMIN</title>
      </Helmet>
      <PageHeader
        title="Všechny soutěže (globálně)"
        subtitle="Cross-unit přehled všech soutěží v systému. Kliknutím přejdete do administrace."
      />
      {isMobile ? (
        <Stack spacing={1}>
          {isLoading && <Typography color="text.secondary">Načítám…</Typography>}
          {!isLoading && comps.length === 0 && (
            <Typography color="text.secondary">Žádné soutěže.</Typography>
          )}
          {comps.map((c) => (
            <Paper
              key={c.id}
              variant="outlined"
              sx={{ p: 2, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
              onClick={() => navigate(`/admin/competitions/${c.id}?unitId=${c.unitId}`)}
            >
              <Typography fontWeight={600}>{c.name}</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                {c.unitName} • {formatDateCz(c.competitionDate)}
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                <Chip size="small" label={COMPETITION_STATUS_LABELS[c.status]} color={STATUS_COLOR[c.status]} />
                <Typography variant="caption" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                  {c.shortCode}
                </Typography>
              </Stack>
            </Paper>
          ))}
        </Stack>
      ) : (
        <Box sx={{ height: 'calc(100vh - 220px)', bgcolor: 'background.paper', borderRadius: 1 }}>
          <DataGrid
            rows={comps}
            columns={columns}
            loading={isLoading}
            disableRowSelectionOnClick
            onRowClick={({ row }) => navigate(`/admin/competitions/${row.id}?unitId=${row.unitId}`)}
            sx={{ border: 0, '& .MuiDataGrid-row': { cursor: 'pointer' } }}
          />
        </Box>
      )}
      <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
        <Typography variant="caption" color="text.secondary">
          Jako SUPER_ADMIN máte přístup ke všem soutěžím bez ohledu na jednotku.
        </Typography>
      </Stack>
    </>
  );
};
